import React from "react";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { PhotoProvider, PhotoSlider } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import type { PhotoProviderProps } from "react-photo-view/dist/PhotoProvider";
import { DataType } from "react-photo-view/dist/types";
import { IPhotoSliderProps } from "react-photo-view/dist/PhotoSlider";

export interface PreviewImagesRef {
  open: ({ index, urls }: { index: number; urls: string[] }) => void;
  close: () => void;
}

type PreviewImagesProps = {
  onClose?: () => void;
  photoProviderProps?: PhotoProviderProps;
} & Omit<IPhotoSliderProps, "visible" | "images" | "onClose">;

const PreviewImages = forwardRef<PreviewImagesRef, PreviewImagesProps>(
  (props, ref) => {
    const { photoProviderProps, ...restPhotoSliderProps } = props;
    const [visible, setVisible] = useState<boolean>(false);
    const [index, setIndex] = useState<number>(0);
    const [images, setImages] = useState<DataType[]>([]);

    const onIndexChange = useCallback(
      (e) => {
        setIndex(e);
        restPhotoSliderProps?.onIndexChange?.(e);
      },
      [restPhotoSliderProps]
    );

    const onClose = useCallback(() => {
      setVisible(false);
      restPhotoSliderProps?.onClose?.();
    }, [restPhotoSliderProps]);

    useImperativeHandle(
      ref,
      () => ({
        open: ({ index: _index, urls }) => {
          const _images = Array.isArray(urls)
            ? urls.map((url, i) => ({ src: url || "", key: i }))
            : [];
          setImages(_images);
          setIndex(_index);
          setVisible(true);
        },
        close: onClose,
      }),
      [onClose]
    );

    return (
      <PhotoProvider {...(props?.photoProviderProps || {})}>
        <PhotoSlider
          photoClosable={false}
          {...(restPhotoSliderProps || {})}
          images={images}
          index={index}
          visible={visible}
          onClose={onClose}
          onIndexChange={onIndexChange}
        />
      </PhotoProvider>
    );
  }
);
export default PreviewImages;
